import React, { useEffect, useMemo, useState } from "react";
const OCCTOO_API_BASEURL = process.env.REACT_APP_OCCTOO_BASEURL;

/* =========================================================
HELPERS
========================================================= */

function safeParseEtimData(article) {
  if (typeof article?.etimData !== "string") return [];
  try {
    return JSON.parse(article.etimData);
  } catch {
    return [];
  }
}

function parseEtimValue(valueString) {
  if (typeof valueString !== "string") return [];
  return valueString
    .split("|")
    .map((v) => v.trim())
    .filter(Boolean);
}

function getArticlesForDropdown(
  products,
  selectableFeatureIds,
  selectedValues,
  currentFid,
) {
  return products.filter((article) => {
    const etim = safeParseEtimData(article);

    return selectableFeatureIds.every((fid) => {
      if (fid === currentFid) return true; // ignorera aktuell dropdown
      const selected = selectedValues[fid];
      if (!selected) return true;

      const f = etim.find((x) => x.FeatureId === fid);
      if (!f) return false;

      if (f.CvlValues?.some((cv) => cv.Key === selected)) return true;

      return (
        f.Value?.stringMap?.["sv-SE"] === selected ||
        f.Value?.stringMap?.["en"] === selected
      );
    });
  });
}

function formatLogicalValue(raw, feature) {
  // Säkerställ att vi verkligen är på en logical-feature
  if (!feature?.featureDatatype && !feature?.featureType) {
    return null;
  }

  const type = (
    feature.featureDatatype ||
    feature.featureType ||
    ""
  ).toLowerCase();

  if (type !== "logical" && type !== "boolean") {
    return null;
  }

  if (typeof raw !== "string") return null;

  const v = raw.trim().toLowerCase();

  if (["true", "yes", "1"].includes(v)) return "Ja";
  if (["false", "no", "0"].includes(v)) return "Nej";

  return null;
}

function groupEtimDataByFeatureGroup(etimData, features) {
  const groups = {};

  etimData.forEach((item) => {
    const feature = features[item.FeatureId];
    const group = feature?.featureGroups?.[0] || null;

    const groupCode = group?.featureGroupCode || "UNGROUPED";
    const groupName = group?.featureGroupDisplayName || "Ingen gruppering alls";

    if (!groups[groupCode]) {
      groups[groupCode] = {
        groupCode,
        groupName,
        items: [],
      };
    }

    groups[groupCode].items.push(item);
  });

  return Object.values(groups);
}
/* =========================================================
API CACHES
========================================================= */

const featureCache = {};
const valueCache = {};

/* =========================================================
API FETCHERS
========================================================= */

async function fetchProducts(productNumber) {
  const res = await fetch(
    `${OCCTOO_API_BASEURL}/products?productNumber=${encodeURIComponent(
      productNumber,
    )}&language=en`,
  );
  const data = await res.json();
  return data?.results ?? [];
}

async function fetchAmClass(classCode) {
  const res = await fetch(
    `${OCCTOO_API_BASEURL}/amclasses?classCode=${classCode}`,
  );
  const data = await res.json();
  return data?.results?.[0] ?? null;
}

async function fetchAmFeature(featureId) {
  if (!featureId) return null;
  if (featureCache[featureId]) return featureCache[featureId];

  const res = await fetch(
    `${OCCTOO_API_BASEURL}/amfeature?featureId=${featureId}&language=sv`,
  );
  const data = await res.json();
  const feature = data?.results?.[0] ?? null;

  if (feature) featureCache[featureId] = feature;
  return feature;
}

async function fetchAmValue(valueCode) {
  if (!valueCode) return null;
  if (valueCache[valueCode]) return valueCache[valueCode];

  const res = await fetch(
    `${OCCTOO_API_BASEURL}/amvalue?valueCode=${valueCode}`,
  );
  const data = await res.json();
  const value = data?.results?.[0] ?? null;

  if (value) valueCache[valueCode] = value;
  return value;
}

/* =========================================================
IMAGE GALLERY (EXAKT SOM GAMMAL)
========================================================= */

function getImageUrlsFromResources(resources) {
  if (!Array.isArray(resources)) return [];
  return resources
    .map((r) => r.urlThumbnail || r.urlImageOriginal || r.url || r.urlOriginal)
    .filter(Boolean);
}

function ProductImageGalleryWithThumbs({ resources }) {
  const images = getImageUrlsFromResources(resources);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images.length) return null;

  return (
    <div>
      {/* MAIN IMAGE */}
      <div
        style={{
          width: "100%",
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafafa",
          border: "1px solid #eee",
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <img
          src={images[activeIndex]}
          alt=""
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      </div>

      {/* THUMBNAILS */}
      {images.length > 1 && (
        <div className="d-flex gap-2 justify-content-center">
          {images.map((src, idx) => (
            <div
              key={idx}
              onClick={() => setActiveIndex(idx)}
              style={{
                width: 60,
                height: 60,
                border:
                  idx === activeIndex ? "2px solid #333" : "1px solid #ddd",
                borderRadius: 4,
                cursor: "pointer",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={src}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
PRODUCT PAGE
========================================================= */

export default function ProductPage() {
  const [productNumber, setProductNumber] = useState("");
  const [products, setProducts] = useState([]);
  const [amClass, setAmClass] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* UI toggles – återlagda */
  const [showFeatureImages, setShowFeatureImages] = useState(false);
  const [showFeatureTextWithImage, setShowFeatureTextWithImage] =
    useState(true);
  const [showValueImages, setShowValueImages] = useState(true);
  const [showValueTextWithImage, setShowValueTextWithImage] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);

  /* ========================================================= */

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setProducts([]);
      setSelectedValues({});
      setAmClass(null);

      const prods = await fetchProducts(productNumber);
      if (!prods.length) {
        setError("Ingen produkt hittades.");
        return;
      }

      setProducts(prods);

      const cls = await fetchAmClass(prods[0].etimClass);
      setAmClass(cls);
    } catch (e) {
      console.error(e);
      setError("Fel vid hämtning.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
SELECTABLE FEATURES – RÄTT ORDNING FRÅN AMCLASS
========================================================= */

  const selectableFeatureIds = useMemo(() => {
    if (!products.length || !amClass) return [];

    const selectableSet = new Set();

    products.forEach((p) => {
      [
        p.productSelectAttrConfigKey,
        p.productSelectAttrConfigKey2,
        p.productSelectAttrConfigKey3,
        p.productSelectAttrConfigKey4,
        p.productSelectAttrConfigKey5,
      ]
        .filter(Boolean)
        .forEach((fid) => selectableSet.add(fid));
    });

    return amClass.includedFeatures
      .filter((f) => selectableSet.has(f.includedFeatureFeatureId))
      .sort(
        (a, b) =>
          Number(a.includedFeatureIndexOrder) -
          Number(b.includedFeatureIndexOrder),
      )
      .map((f) => f.includedFeatureFeatureId);
  }, [products, amClass]);
  // ✅ DEBUG – ska ligga FÖRE filteredArticles och EFTER selectableFeatureIds
  useEffect(() => {
    console.log("Selectable feature IDs:", selectableFeatureIds);
    console.log("Products count:", products.length);
    console.log(
      "SelectAttr keys per product:",
      products.map((p) => ({
        itemNumber: p.itemNumber,
        k1: p.productSelectAttrConfigKey,
        k2: p.productSelectAttrConfigKey2,
        k3: p.productSelectAttrConfigKey3,
        k4: p.productSelectAttrConfigKey4,
        k5: p.productSelectAttrConfigKey5,
      })),
    );
  }, [selectableFeatureIds, products]);
  /* =========================================================
SELECTED ARTICLE
========================================================= */

  const filteredArticles = useMemo(() => {
    if (!products.length) return [];

    return products.filter((article) => {
      const etim = safeParseEtimData(article);

      return selectableFeatureIds.every((fid) => {
        const selectedValue = selectedValues[fid];
        if (!selectedValue) return true;

        const f = etim.find((x) => x.FeatureId === fid);
        if (!f) return false;

        // CVL
        if (f.CvlValues?.some((cv) => cv.Key === selectedValue)) return true;

        // Alphanumeric
        return (
          f.Value?.stringMap?.["sv-SE"] === selectedValue ||
          f.Value?.stringMap?.["en"] === selectedValue
        );
      });
    });
  }, [products, selectedValues, selectableFeatureIds]);

  /* =========================================================
RENDER
========================================================= */

  return (
    <div className="container mt-4">
      <h1>Produkt</h1>

      <div className="mb-3">
        <label className="form-label">Produktnummer</label>
        <input
          className="form-control"
          value={productNumber}
          onChange={(e) => setProductNumber(e.target.value)}
        />
        <button className="btn btn-dark mt-2" onClick={load} disabled={loading}>
          {loading ? "Söker…" : "Hämta"}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* TOGGLES – återställda */}
      <div className="d-flex gap-3 my-4 flex-wrap">
        <label>
          <input
            type="checkbox"
            checked={showFeatureImages}
            onChange={(e) => setShowFeatureImages(e.target.checked)}
          />
          Feature‑bild
        </label>
        <label>
          <input
            type="checkbox"
            checked={showFeatureTextWithImage}
            onChange={(e) => setShowFeatureTextWithImage(e.target.checked)}
          />{" "}
          Feature‑text vid bild
        </label>
        <label>
          <input
            type="checkbox"
            checked={showValueImages}
            onChange={(e) => setShowValueImages(e.target.checked)}
          />
          Value‑bild
        </label>
        <label>
          <input
            type="checkbox"
            checked={showValueTextWithImage}
            onChange={(e) => setShowValueTextWithImage(e.target.checked)}
          />{" "}
          Value‑text vid bild
        </label>
        <label>
          <input
            type="checkbox"
            checked={showTooltips}
            onChange={(e) => setShowTooltips(e.target.checked)}
          />
          Tooltips
        </label>
      </div>

      {/* DROPDOWNS – selectable attributes */}
      {selectableFeatureIds.map((fid) => {
        const includedFeature = amClass?.includedFeatures?.find(
          (f) => f.includedFeatureFeatureId === fid,
        );

        const usedCvlKeys = new Set();
        const stringValues = new Set();

        const basisArticles = getArticlesForDropdown(
          products,
          selectableFeatureIds,
          selectedValues,
          fid,
        );

        basisArticles.forEach((p) => {
          const etim = safeParseEtimData(p);
          const f = etim.find((x) => x.FeatureId === fid);
          if (!f) return;

          // Samla CVL-keys (om de finns)
          if (Array.isArray(f.CvlValues) && f.CvlValues.length > 0) {
            f.CvlValues.forEach((cv) => usedCvlKeys.add(cv.Key));
          }

          // Samla string-values (aldrig kopplat till CVL-definition)
          const val =
            f.Value?.stringMap?.["sv-SE"] || f.Value?.stringMap?.["en"];
          if (val) stringValues.add(val);
        });

        const hasEtimCvlDefinition =
          includedFeature &&
          Array.isArray(includedFeature.includedFeatureValues) &&
          includedFeature.includedFeatureValues.length > 0;

        let options = [];

        /* ✅ FALL A – RIKTIG CVL (ETIM-styrd) */
        if (hasEtimCvlDefinition) {
          options = includedFeature.includedFeatureValues
            .filter((evCode) => usedCvlKeys.has(evCode))
            .map((evCode) => {
              const meta = includedFeature.values.find(
                (v) => v.valueCode === evCode,
              );
              return {
                key: evCode,
                label: meta?.valueDisplayName || meta?.valueName || evCode,
              };
            });
        } else {
          /* ✅ FALL B – STRING / NON-CVL */
          options = Array.from(stringValues).map((val) => ({
            key: val,
            label: val,
          }));
        }

        if (!options.length) return null;

        return (
          <div key={fid} className="mb-3">
            <label className="form-label">
              {getFeatureLabel(fid, amClass, products)}
            </label>
            <select
              className="form-select"
              value={selectedValues[fid] || ""}
              onChange={(e) =>
                setSelectedValues((prev) => ({
                  ...prev,
                  [fid]: e.target.value,
                }))
              }
            >
              <option value="">Välj</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      {filteredArticles.map((article) => (
        <ArticleCard
          key={article.itemNumber}
          article={article}
          showFeatureImages={showFeatureImages}
          showFeatureTextWithImage={showFeatureTextWithImage}
          showValueImages={showValueImages}
          showValueTextWithImage={showValueTextWithImage}
          showTooltips={showTooltips}
        />
      ))}
    </div>
  );
}

function getFeatureLabel(fid, amClass, products, language = "sv-SE") {
  // 1️⃣ Försök via amClass (ETIM-metadata)
  const included = amClass?.includedFeatures?.find(
    (f) => f.includedFeatureFeatureId === fid,
  );

  if (included?.includedFeatureName?.stringMap?.[language]) {
    return included.includedFeatureName.stringMap[language];
  }

  // 2️⃣ Fallback: ta namnet från första artikeln som har featuren
  for (const p of products) {
    const etim = safeParseEtimData(p);
    const feature = etim.find((x) => x.FeatureId === fid);
    if (feature?.Name?.stringMap?.[language]) {
      return feature.Name.stringMap[language];
    }
  }

  // 3️⃣ Sista fallback
  return fid;
}
/* =========================================================
ARTICLE CARD – full rendering, som innan
========================================================= */

function collectValueIcons(etimData, valueMeta, features) {
  const seen = new Set(); // ✅ deduplisera PÅ IKON
  const icons = [];

  etimData.forEach((item) => {
    const feature = features[item.FeatureId];

    /* =====================================================
       1️⃣ CVL values – värdeikoner
    ===================================================== */
    item.CvlValues?.forEach((cv) => {
      const meta = valueMeta[cv.Key];
      const img = meta?.valueImage;

      if (img && !seen.has(img)) {
        // ✅ dedupe på image
        seen.add(img);
        icons.push({
          key: cv.Key,
          src: img,
          tooltip: meta.valueTooltip || meta.valueDescription,
        });
      }
    });

    /* =====================================================
       2️⃣ LOGICAL feature – featureikon om TRUE
    ===================================================== */
    if (
      feature?.featureImage &&
      !item.CvlValues?.length &&
      item.Value?.stringMap
    ) {
      const raw = item.Value.stringMap["en"] ?? item.Value.stringMap["sv-SE"];

      const isTrue =
        typeof raw === "string" &&
        ["true", "yes", "1"].includes(raw.toLowerCase());

      const img = feature.featureImage;

      if (isTrue && img && !seen.has(img)) {
        // ✅ dedupe på image
        seen.add(img);
        icons.push({
          key: item.FeatureId,
          src: img,
          tooltip: feature.featureTooltip || feature.featureDescription,
        });
      }
    }
  });

  return icons;
}

function InfoIcon({ text, enabled }) {
  if (!enabled || !text) return null;

  return (
    <span
      tabIndex={0}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: "1px solid #999",
        fontSize: 11,
        fontWeight: "bold",
        cursor: "help",
        color: "#555",
        userSelect: "none",
        position: "relative",
      }}
      aria-label="Information"
      title={text} // fallback för native tooltip
    >
      i
    </span>
  );
}

function ArticleCard({
  article,
  showFeatureImages,
  showFeatureTextWithImage,
  showValueImages,
  showValueTextWithImage,
  showTooltips,
}) {
  const [features, setFeatures] = useState({});
  const [valueMeta, setValueMeta] = useState({});
  const etimData = safeParseEtimData(article);
  const valueIcons = collectValueIcons(etimData, valueMeta, features);

  const groupedEtimData = useMemo(() => {
    return groupEtimDataByFeatureGroup(etimData, features);
  }, [etimData, features]);

  useEffect(() => {
    const loadMeta = async () => {
      const fMap = {};
      const vMap = {};

      for (const item of etimData) {
        if (!fMap[item.FeatureId]) {
          const f = await fetchAmFeature(item.FeatureId);
          if (f) fMap[item.FeatureId] = f;
        }
        for (const cv of item.CvlValues || []) {
          if (!vMap[cv.Key]) {
            const v = await fetchAmValue(cv.Key);
            if (v) vMap[cv.Key] = v;
          }
        }
      }

      setFeatures(fMap);
      setValueMeta(vMap);
    };

    if (etimData.length) loadMeta();
  }, [article?.id]);

  return (
    <div className="card mb-4">
      {/* HEADER */}
      <div className="card-header">
        <strong>{article.productName}</strong>
        <div className="text-muted small">{article.itemNumber}</div>
      </div>

      <div className="card-body">
        {/* TOP SECTION */}
        <div className="row g-4 mb-4">
          {/* LEFT – ARTICLE INFO */}
          <div className="col-md-6">
            <table className="table table-sm align-middle mb-0">
              <tbody>
                <tr>
                  <td className="text-muted bold-text">Artikelnummer</td>
                  <td>{article.itemNumber}</td>
                </tr>
                <tr>
                  <td className="text-muted bold-text">ETIM‑klass</td>
                  <td>{article.etimClass}</td>
                </tr>
                <tr>
                  <td className="text-muted bold-text">Produktbeskrivning</td>
                  <td>{article.productWebDescription}</td>
                </tr>
                <tr>
                  <td className="text-muted bold-text">Artikelbeskrivning</td>
                  <td>{article.itemWebDescription}</td>
                </tr>
                <tr>
                  <td className="text-muted bold-text">NOBB‑nummer</td>
                  <td>{article.itemNobbNr}</td>
                </tr>

                {/* REDO FÖR SENARE */}
                {/* 
                <tr>
                  <td className="text-muted">Varumärke</td>
                  <td>{article.brand}</td>
                </tr> 
                */}
              </tbody>
            </table>
          </div>

          {/* RIGHT – IMAGES */}
          <div className="col-md-6">
            <ProductImageGalleryWithThumbs resources={article.resources} />

            {valueIcons.length > 0 && (
              <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                {valueIcons.map((icon) => (
                  <div
                    key={icon.key}
                    title={icon.key}
                    style={{
                      width: 32,
                      height: 32,
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={icon.src}
                      alt=""
                      style={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ETIM DATA */}
        <h4 className="mb-3">Tekniska egenskaper</h4>

        <table className="table table-sm align-middle">
          <tbody>
            {groupedEtimData.map((group) => (
              <React.Fragment key={group.groupCode}>
                <tr>
                  <td
                    colSpan={2}
                    style={{ paddingTop: 16 }}
                    title={group.groupCode}
                  >
                    <h5>{group.groupName}</h5>
                  </td>
                </tr>

                {group.items.map((item) => {
                  const feature = features[item.FeatureId];
                  const raw =
                    item?.Value?.stringMap?.["sv-SE"] ??
                    item?.Value?.stringMap?.["en"] ??
                    "";
                  const parts = parseEtimValue(raw);

                  const hasFeatureImg = !!feature?.featureImage;
                  const hasValueImg = item.CvlValues?.some(
                    (cv) => valueMeta[cv.Key]?.valueImage,
                  );

                  return (
                    <tr key={item.FeatureId} title={item.FeatureId}>
                      <td style={{ width: "45%" }}>
                        <div className="d-flex align-items-center gap-2">
                          {showFeatureImages && hasFeatureImg && (
                            <img
                              src={feature.featureImage}
                              alt=""
                              style={{ width: 24, height: 24 }}
                            />
                          )}

                          {(!hasFeatureImg || showFeatureTextWithImage) && (
                            <>
                              <span class="bold-text">
                                {item.Name?.stringMap?.["sv-SE"] ??
                                  item.Name?.stringMap?.["en"]}
                              </span>

                              <InfoIcon
                                text={feature?.featureTooltip}
                                enabled={showTooltips}
                              />
                            </>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {showValueImages &&
                            item.CvlValues?.map((cv) => {
                              const v = valueMeta[cv.Key];
                              return (
                                v?.valueImage && (
                                  <img
                                    key={cv.Key}
                                    src={v.valueImage}
                                    alt=""
                                    style={{ width: 24, height: 24 }}
                                  />
                                )
                              );
                            })}

                          {(!hasValueImg || showValueTextWithImage) &&
                            (() => {
                              const logicalLabel = formatLogicalValue(
                                raw,
                                feature,
                              );

                              return (
                                <span
                                  title={
                                    showTooltips
                                      ? valueMeta[item.CvlValues?.[0]?.Key]
                                          ?.valueTooltip || undefined
                                      : undefined
                                  }
                                >
                                  {logicalLabel ??
                                    (parts.length <= 1
                                      ? raw || "-"
                                      : parts.join(", "))}
                                </span>
                              );
                            })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
